/* ZEBAESH — SMART CART */

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = this.querySelector(
      '#cart-notification'
    );

    this.header = document.querySelector('sticky-header');

    this.itemsContainer = this.querySelector(
      '#zeb-cart-items'
    );

    this.cartItemKey = null;
    this.recentVariantId = null;

    this.activeElement = null;
    this.isModalOpen = false;
    this.isUpdating = false;

    this.previousBodyOverflow = '';
    this.requestSequence = 0;
    this.heroImageRequestId = 0;
    this.heroImageCache = new Map();

    // POPUP PREVIEW ONLY: fixed estimated PayFee rewards in PKR rupees.
    // Match these amounts to PayFee app settings; checkout still requests
    // the live discount code and Shopify determines the final payable total.
    // Fixed PREVIEW only. Shopify/PayFee must still accept the reward at checkout.
    // PKR uses minor units: Rs. 100 = 10000 paisa.
    this.fixedPrepaymentReward = 10000;
    this.discountAmount = null;
    this.payfeeOfferRequest = 0;
    this.walletExpectedDiscount = null;
    this.walletOfferRequest = 0;
    this.walletOfferUnavailable = false;
    this.walletOfferPending = false;
    this.offerCartKey = null;
    this.latestOriginalCart = null;

    this.currency = this.dataset.currency || 'PKR';

    this.cartRoot = window.Shopify?.routes?.root || '/';

    this.onClick = this.handleClick.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);

    // The popup records a preference; live PayFee code may enable direct
    // checkout, otherwise the genuine cart-page PayFee widget is used.
    this.payfeeChoice = null;
    this.payfeeSubmitting = false;
    this.clearLegacyPayfeeHandoff();
    this.onPayfeeSubmit = this.handlePayfeeSubmit.bind(this);
    this.querySelector('#cart-notification-form')?.addEventListener(
      'submit', this.onPayfeeSubmit
    );

    // Checkout -> browser Back can restore a frozen page (bfcache) where
    // the button was disabled and payfeeSubmitting was still true.
    this.onReturnFromCheckout = this.handleReturnFromCheckout.bind(this);
    window.addEventListener('pageshow', this.onReturnFromCheckout);
  }

  /* RECOVER CHECKOUT ACTION AFTER BROWSER BACK / BFCACHE */

  handleReturnFromCheckout(event) {
    // A history Back may reload the product page instead of restoring bfcache.
    // Reopen this popup from the existing cart without adding another product.
    let hasReturnMarker = false;
    try {
      const stamp = Number(sessionStorage.getItem('zeb-checkout-return-at-v2'));
      const nav = window.performance?.getEntriesByType?.('navigation')?.[0];
      hasReturnMarker = Number.isFinite(stamp) && stamp > 0 &&
        Date.now() - stamp < 30 * 60 * 1000 &&
        (event.persisted || nav?.type === 'back_forward');
      if (hasReturnMarker) sessionStorage.removeItem('zeb-checkout-return-at-v2');
    } catch (_) {}
    if (hasReturnMarker && !this.isModalOpen) {
      this.fetchCart().then(cart => {
        if (Number(cart?.item_count) > 0 && !this.isModalOpen) {
          this.open();
          this.renderCart(cart);
        }
      }).catch(error => console.warn('[Zebaesh Checkout] Return refresh failed:', error));
    }

    const button = this.find('#zeb-cart-online-checkout');
    // An ordinary fresh load needs no recovery. In a history-restored page,
    // the browser may preserve the disabled button and pending JS flag.
    if (!event.persisted && !this.payfeeSubmitting &&
        !(this.isModalOpen && button?.disabled && !this.isUpdating)) return;

    this.payfeeSubmitting = false;
    this.clearLegacyPayfeeHandoff();
    this.showCheckoutStatus('');
    // Do not blindly enable checkout if cart contents are unknown or empty.
    if (button) {
      const count = Number(this.latestOriginalCart?.item_count);
      button.disabled = this.isUpdating || !Number.isFinite(count) || count <= 0;
    }
    // A return can also happen after an order/cart change on checkout.
    // Re-read Shopify's cart before the next selection or submission.
    // Keep the selected method label and dropdown available for editing.
    void this.refreshCart();
  }

  /* SHOPIFY CART API ROUTES */

  getCartUrl(endpoint) {
    const root = this.cartRoot.endsWith('/')
      ? this.cartRoot
      : `${this.cartRoot}/`;

    const endpoints = {
      get: 'cart.js',
      change: 'cart/change.js',
    };

    const path = endpoints[endpoint];

    if (!path) {
      throw new Error(
        `Unknown cart endpoint: ${endpoint}`
      );
    }

    return `${root}${path}`;
  }

  /* MONEY FORMATTING */

  formatMoney(amount) {
    const value = Math.max(
      0,
      Number(amount) || 0
    ) / 100;

    if (this.currency === 'PKR') {
      return `Rs. ${new Intl.NumberFormat('en-PK', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)}`;
    }

    try {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: this.currency
      }).format(value);
    } catch (error) {
      return `${this.currency} ${value.toFixed(2)}`;
    }
  }

  /* DOM HELPERS */

  find(selector, parent = this) {
    return parent.querySelector(selector);
  }

  setText(selector, value) {
    const element = this.find(selector);

    if (element) {
      element.textContent = String(value);
    }
  }

  createElement(tag, className, text) {
    const element = document.createElement(tag);

    if (className) {
      element.className = className;
    }

    if (text !== undefined && text !== null) {
      element.textContent = String(text);
    }

    return element;
  }

  createButton(
    className,
    label,
    action,
    key,
    quantity
  ) {
    const button = this.createElement(
      'button',
      className,
      label
    );

    button.type = 'button';

    button.dataset.action = action;

    if (key) {
      button.dataset.key = key;
    }

    if (quantity !== undefined) {
      button.dataset.quantity = String(quantity);
    }

    button.setAttribute('aria-label', label);

    return button;
  }

  /* OPEN LUXURY MODAL */

  open() {
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    if (this.isModalOpen) {
      return;
    }

    this.activeElement =
      this.activeElement || document.activeElement;

    this.previousBodyOverflow =
      document.body.style.overflow;

    this.isModalOpen = true;

    document.body.style.overflow = 'hidden';

    // Allow visibility only after an intentional add-to-cart action.
    this.style.removeProperty('visibility');
    this.style.removeProperty('opacity');
    this.style.removeProperty('pointer-events');
    this.removeAttribute('inert');
    this.removeAttribute('aria-hidden');

    this.notification.classList.add(
      'animate',
      'active'
    );

    this.classList.add('is-open');

    this.notification.focus({
      preventScroll: true
    });

    if (typeof trapFocus === 'function') {
      trapFocus(this.notification);
    }
  }

  /* CLOSE LUXURY MODAL */

  close() {
    if (!this.isModalOpen || this.isUpdating) {
      return;
    }

    this.isModalOpen = false;

    this.classList.remove('is-open');

    // Fail closed even when stylesheets load late.
    this.style.visibility = 'hidden';
    this.style.opacity = '0';
    this.style.pointerEvents = 'none';
    this.setAttribute('inert', '');
    this.setAttribute('aria-hidden', 'true');

    this.notification.classList.remove('active');

    document.body.style.overflow =
      this.previousBodyOverflow;

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    } else if (
      this.activeElement &&
      this.activeElement.isConnected &&
      typeof this.activeElement.focus === 'function'
    ) {
      this.activeElement.focus({
        preventScroll: true
      });
    }

    this.activeElement = null;
    try { sessionStorage.removeItem('zeb-checkout-return-at-v2'); } catch (_) {}
  }

  /* KEYBOARD HANDLING */

  handleKeyDown(event) {
    if (
      event.key === 'Escape' &&
      this.isModalOpen
    ) {
      event.preventDefault();
      this.close();
    }
  }

  /* MODAL CLICK HANDLING */

  handleClick(event) {
    if (!this.isModalOpen) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) return;

    const payfeeTrigger = target.closest('#zeb-payfee-trigger');
    if (payfeeTrigger) {
      event.preventDefault();
      const options = this.find('#zeb-payfee-options');
      if (options) {
        const expand = options.hidden;
        options.hidden = !expand;
        payfeeTrigger.setAttribute('aria-expanded', String(expand));
      }
      return;
    }

    const payfeeOption = target.closest('[data-zeb-payfee-method]');
    if (payfeeOption) {
      event.preventDefault();
      this.selectPayfeeMethod(payfeeOption.dataset.zebPayfeeMethod);
      return;
    }

    const closeButton = target.closest(
      '.cart-notification__close, .zeb-cart__continue'
    );

    if (closeButton) {
      event.preventDefault();
      this.close();
      return;
    }

    const wrapper = this.find(
      '.cart-notification-wrapper'
    );

    if (target === wrapper) {
      this.close();
      return;
    }

    const actionButton = target.closest(
      '[data-action]'
    );

    if (!actionButton || this.isUpdating) {
      return;
    }

    const action = actionButton.dataset.action;
    const key = actionButton.dataset.key;

    const quantity = Number(
      actionButton.dataset.quantity
    );

    if (!key) {
      return;
    }

    if (action === 'increase') {
      this.changeQuantity(key, quantity + 1);
    } else if (action === 'decrease') {
      this.changeQuantity(
        key,
        Math.max(0, quantity - 1)
      );
    } else if (action === 'remove') {
      this.changeQuantity(key, 0);
    }
  }

  /* POPUP PAYMENT PREFERENCE — PAYFEE HANDOFF */

  selectPayfeeMethod(method) {
    if (!['cod', 'bank', 'wallet'].includes(method) || this.payfeeSubmitting) return;
    this.payfeeChoice = method;
    this.showCheckoutStatus('');
    // Keep the compact selector label synced to the selected payment method.
    const methodLabels = {
      wallet: 'Jazzcash / Easypaisa / Raast ID',
      bank: 'Bank Deposit',
      cod: 'Cash on Delivery (COD)'
    };
    this.setText('#zeb-payfee-selected', methodLabels[method]);
    this.querySelectorAll('[data-zeb-payfee-method]').forEach(option => {
      option.setAttribute('aria-pressed', String(option.dataset.zebPayfeeMethod === method));
    });
    const options = this.find('#zeb-payfee-options');
    const trigger = this.find('#zeb-payfee-trigger');
    if (options) options.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (this.latestOriginalCart) this.renderTotals(this.latestOriginalCart);
    this.updateVisibleVoucher();
    // Reward estimates are configured locally. No PayFee preview API calls.
    // Real eligibility and coupon application are checked during checkout.
  }

  updateVisibleVoucher() {
    const pricing = this.latestOriginalCart && this.getCartPricing(this.latestOriginalCart);
    const value = pricing && pricing.baseSubtotal >= this.fixedPrepaymentReward &&
      (this.latestOriginalCart.currency || this.currency) === 'PKR'
      ? this.fixedPrepaymentReward : null;
    const valid = Number.isSafeInteger(value) && value > 0;
    this.setText('#zeb-cart-voucher-amount', valid
      ? `${this.formatMoney(value)} OFF` : 'PREPAID OFFER');
    this.setText('#zeb-cart-voucher-heading-amount', valid
      ? `Unlock ${this.formatMoney(value)} Savings` : 'Unlock Prepaid Savings');
  }

  /* PREVIEW: FIXED MERCHANT-CONFIGURED REWARDS; NO NETWORK REQUESTS */

  /* CHECKOUT: DIRECT ROUTING ONLY — NEVER REDIRECT TO /CART */

  clearLegacyPayfeeHandoff() {
    // Remove abandoned /cart auto-forward state from older theme versions.
    try {
      ['zeb-payfee-choice-v1', 'zeb-payfee-autoforward-v1',
        'zeb-payfee-expected-discount-v1'].forEach(key => sessionStorage.removeItem(key));
    } catch (_) {}
  }

  showCheckoutStatus(message) {
    const status = this.find('#zeb-cart-checkout-status');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
  }

  async handlePayfeeSubmit(event) {
    event.preventDefault();
    if (this.isUpdating || this.payfeeSubmitting) return;

    const choice = this.payfeeChoice;
    const button = this.find('#zeb-cart-online-checkout');
    if (!['bank', 'cod', 'wallet'].includes(choice)) {
      const options = this.find('#zeb-payfee-options');
      const trigger = this.find('#zeb-payfee-trigger');
      if (options) options.hidden = false;
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      this.showCheckoutStatus('Please choose a payment method first.');
      return;
    }

    this.payfeeSubmitting = true;
    if (button) button.disabled = true;
    this.showCheckoutStatus('');
    this.clearLegacyPayfeeHandoff();
    let navigating = false;

    try {
      const cart = await this.fetchCart();
      if (!Number(cart?.item_count)) {
        throw new Error('Your cart is empty. Please add a product first.');
      }
      if (this.payfeeChoice !== choice) throw new Error('Please select the payment method again.');

      const checkoutURL = new URL('checkout', new URL(
        this.cartRoot.endsWith('/') ? this.cartRoot : this.cartRoot + '/',
        window.location.origin
      ));

      if (choice === 'cod') {
        // Only remove a previous PayFee coupon when it is present.
        // Shopify automatic discounts do NOT need to be zero for COD.
        await this.preparePayfeeCodDirectCheckout(cart);
      } else {
        const rule = this.getPayfeeRuleId(choice);
        if (!rule) throw new Error('This payment method is not configured.');
        const code = await this.requestPayfeeBankDiscountCode(rule, choice, cart);
        if (!code) throw new Error('The payment offer could not be prepared. Please try again.');
        checkoutURL.searchParams.set('discount', code);
      }

      if (this.payfeeChoice !== choice) throw new Error('Payment method changed. Please try again.');
      // The payment preference is advisory; the buyer still chooses their
      // actual payment gateway during Shopify checkout.
      this.clearLegacyPayfeeHandoff();
      try { sessionStorage.setItem('zeb-checkout-return-at-v2', String(Date.now())); } catch (_) {}
      navigating = true;
      window.location.assign(checkoutURL.href);
    } catch (error) {
      console.error('[Zebaesh Checkout] Direct handoff failed:', error);
      this.showCheckoutStatus(error?.message || 'Checkout is temporarily unavailable. Please try again.');
    } finally {
      if (!navigating) {
        this.payfeeSubmitting = false;
        if (button) button.disabled = this.isUpdating || !Number(this.latestOriginalCart?.item_count);
      }
    }
  }

  /* DISCOUNT CODES — MANAGE PAYFEE INDEPENDENTLY OF OTHER PROMOTIONS */

  getAppliedDiscountCodes(cart) {
    // Shopify's cart-level code list and discount applications can overlap.
    // Never infer a code from an automatic discount title.
    const codes = [];
    for (const entry of [
      ...(cart?.discount_codes || []),
      ...(cart?.cart_level_discount_applications || []),
      ...(cart?.discount_applications || []),
      ...(cart?.items || []).flatMap(item =>
        (item.discount_allocations || item.line_level_discount_allocations || [])
          .map(allocation => allocation.discount_application || {}))
    ]) {
      const raw = String(entry?.code || '').trim();
      const title = String(entry?.title || '').trim();
      const code = raw || (this.isPayfeeCode(title) ? title : '');
      if (code && !codes.some(existing => existing.toLowerCase() === code.toLowerCase())) {
        codes.push(code);
      }
    }
    return codes;
  }

  isPayfeeCode(code) {
    return /^PBMD[A-Za-z0-9]+$/i.test(String(code || ''));
  }

  getPayfeeAllocatedAmount(cart) {
    // Where Shopify exposes allocations, add back *only* the payment reward.
    // All non-PayFee discounts (including Pumper) remain in the base price.
    const applications = cart?.cart_level_discount_applications || [];
    let amount = applications.reduce((total, application) => {
      if (!this.isPayfeeCode(application.code)) return total;
      const allocated = Number(application.total_allocated_amount);
      return total + (Number.isSafeInteger(allocated) && allocated > 0 ? allocated : 0);
    }, 0);
    for (const item of cart?.items || []) {
      const list = item.discount_allocations || item.line_level_discount_allocations || [];
      for (const allocation of list) {
        const application = allocation.discount_application ||
          cart?.discount_applications?.[allocation.discount_application_index] || {};
        if (!this.isPayfeeCode(application.code)) continue;
        const allocated = Number(allocation.amount);
        if (Number.isSafeInteger(allocated) && allocated > 0) amount += allocated;
      }
    }
    return amount;
  }

  async updateCartPaymentPreference(method, codes = null) {
    const labels = {
      cod: 'Cash on Delivery (COD)',
      bank: 'Bank Deposit',
      wallet: 'Jazzcash / Easypaisa / Raast ID'
    };
    const body = { attributes: { paymentsMethod: labels[method] } };
    // Omit 'discount' completely when only setting the payment preference.
    // Shopify discount: '' clears ALL codes; never use it blindly.
    if (codes !== null) body.discount = codes.join(',');
    const response = await fetch(this.cartRoot.replace(/\/?$/, '/') + 'cart/update.js', {
      method: 'POST', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('Could not update payment preference. Please retry.');
    return this.fetchCart();
  }

  async preparePayfeeCodDirectCheckout(before) {
    if (!Number(before?.item_count)) throw new Error('Your cart is empty.');
    const allCodes = this.getAppliedDiscountCodes(before);
    const otherCodes = allCodes.filter(code => !this.isPayfeeCode(code));
    const hasPayfee = allCodes.some(code => this.isPayfeeCode(code));
    // If there is no PayFee coupon, do NOT touch discount codes.
    const after = await this.updateCartPaymentPreference('cod', hasPayfee ? otherCodes : null);
    if (!this.sameCartItems(before, after)) {
      throw new Error('Cart products changed during checkout preparation. Please retry.');
    }
    if (this.getAppliedDiscountCodes(after).some(code => this.isPayfeeCode(code))) {
      throw new Error('Previous prepaid reward is still active. Please retry.');
    }
    // Existing non-payment codes must not be silently lost.
    const remaining = this.getAppliedDiscountCodes(after).map(code => code.toLowerCase());
    if (otherCodes.some(code => !remaining.includes(code.toLowerCase()))) {
      throw new Error('A product or bundle discount was not retained. Please check discount combination settings.');
    }
    try { sessionStorage.removeItem('zeb-active-payfee-receipt-v4'); } catch (_) {}
    this.latestOriginalCart = after;
    this.renderTotals(after);
    return true;
  }

  sameCartItems(a, b) {
    const signature = cart => JSON.stringify((cart.items || []).map(item => ({
      variantId: String(item.variant_id || ''),
      quantity: Number(item.quantity),
      // Compare variant sale price, not discounted final_line_price or a
      // cart discount amount which may legitimately change.
      saleUnit: Number(item.original_price),
      properties: item.properties || {},
      plan: String(item.selling_plan_allocation?.selling_plan?.id || '')
    })).sort((x, y) => JSON.stringify(x).localeCompare(JSON.stringify(y))));
    return Number(a.item_count) === Number(b.item_count) && signature(a) === signature(b);
  }

  /* VERIFIED PAYFEE RULE IDS — OBSERVED FROM ORIGINAL /cart INPUTS
     Bank Deposit = 1817; Wallet / Raast = 1819. Discount values
     remain dynamic and come only from PayFee's live API response. */
  getPayfeeRuleId(method) {
    if (method === 'bank') return 1817;
    if (method === 'wallet') return 1819;
    return null;
  }

  /* PAYFEE OFFER AMOUNT — FROM LIVE RESPONSE, NOT HARDCODED */

  async fetchPayfeeOffer(cart, signal, ruleId = 1817) {
    // The observed PayFee endpoint is not a documented public API.
    // If it stops responding, display unavailable values instead of
    // showing an incorrect promised discount.
    const cartTotal = this.getCartBaseSubtotal(cart);
    const originalTotal = cartTotal;
    if (!Number.isSafeInteger(cartTotal) || cartTotal <= 0 ||
        !Number.isSafeInteger(originalTotal) || originalTotal <= 0 ||
        (cart.currency || this.currency) !== 'PKR') {
      throw new Error('Unsupported cart or currency');
    }
    const applications = [
      ...(Array.isArray(cart.cart_level_discount_applications)
        ? cart.cart_level_discount_applications : []),
      ...(Array.isArray(cart.discount_applications)
        ? cart.discount_applications : [])
    ];
    const previousCode = applications
      .map(entry => String(entry.code || entry.title || ''))
      .find(code => /^PBMD[A-Za-z0-9]+$/i.test(code)) ||
      this.getAppliedDiscountCodes(cart).find(code => this.isPayfeeCode(code)) || '';

    const endpoint = new URL(
      'https://payment-method-based-fee-discount.addify-app.com/api/get-discount-code-or-fee-generate'
    );
    endpoint.searchParams.set('prev_discount_code', previousCode);
    if (!Number.isSafeInteger(Number(ruleId)) || Number(ruleId) <= 0) {
      throw new Error('Unknown payment rule');
    }
    endpoint.searchParams.set('selected_rule_id', String(ruleId));
    endpoint.searchParams.set('cartTotal', String(cartTotal));
    endpoint.searchParams.set('shopName',
      window.Shopify?.shop || '134j09-k9.myshopify.com');

    const response = await fetch(endpoint.href, {
      method: 'GET', mode: 'cors', credentials: 'omit',
      cache: 'no-store', signal,
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`PayFee HTTP ${response.status}`);
    const reply = await response.json();
    const code = String(reply?.discount_code || '').trim();
    const rupees = Number(reply?.discount_amount);
    const amount = Math.round(rupees * 100);
    if (!/^PBMD[A-Za-z0-9]{8,80}$/i.test(code) ||
        !Number.isFinite(rupees) || rupees <= 0 ||
        !Number.isSafeInteger(amount) || amount > originalTotal ||
        reply.variant_id != null) {
      throw new Error('PayFee offer response invalid or not a fixed order discount');
    }
    if (amount !== this.fixedPrepaymentReward) {
      throw new Error('PayFee reward does not match the configured Rs. 100. Verify PayFee settings.');
    }
    return { code, amount };
  }

  /* PREPAID — LIVE PAYFEE CODE, APPLY TO CART, THEN DIRECT CHECKOUT */

  async requestPayfeeBankDiscountCode(ruleId, choice, beforeCart) {
    if (!Number(beforeCart?.item_count)) throw new Error('Your cart is empty.');
    if ((beforeCart.currency || this.currency) !== 'PKR') {
      throw new Error('This payment offer requires a PKR cart.');
    }
    let offer;
    let lastError;
    for (let attempt = 0; attempt < 2; attempt++) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 8000);
      try {
        offer = await this.fetchPayfeeOffer(beforeCart, controller.signal, ruleId);
        break;
      } catch (error) {
        lastError = error;
      } finally {
        window.clearTimeout(timeout);
      }
    }
    if (!offer) {
      console.warn('[Zebaesh PayFee] Could not generate a prepaid code:', lastError);
      throw new Error('PayFee could not confirm the prepaid discount. Please retry shortly.');
    }

    if (this.payfeeChoice !== choice) throw new Error('Payment preference changed. Please retry.');
    // Preserve Pumper/customer coupon codes. Only replace the old PayFee code.
    const originalCodes = this.getAppliedDiscountCodes(beforeCart)
      .filter(code => !this.isPayfeeCode(code));
    const afterCart = await this.updateCartPaymentPreference(choice, [...originalCodes, offer.code]);
    const afterCodes = this.getAppliedDiscountCodes(afterCart).map(code => code.toLowerCase());
    if (originalCodes.some(code => !afterCodes.includes(code.toLowerCase()))) {
      throw new Error('Your existing bundle or product coupon could not combine with PayFee. Please review discount combination settings.');
    }
    if (!this.sameCartItems(beforeCart, afterCart)) {
      throw new Error('Cart items changed during payment preparation. Please retry.');
    }
    this.latestOriginalCart = afterCart;
    this.rememberPayfeeCheckout(afterCart, offer.code, offer.amount);
    if (choice === 'wallet') this.walletExpectedDiscount = offer.amount;
    else this.discountAmount = offer.amount;
    this.renderTotals(afterCart);
    this.updateVisibleVoucher();
    // A code returned by an undocumented third-party endpoint is not a
    // guarantee that Shopify checkout will apply it. Shopify validates there.
    return offer.code;
  }

  /* DAWN AJAX CONTENT RENDERING */

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key || null;

    this.recentVariantId =
      parsedState.variant_id ||
      parsedState.id ||
      null;

    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(
        section.id
      );

      const sectionHTML =
        parsedState.sections?.[section.id];

      if (!target || !sectionHTML) {
        return;
      }

      const updatedHTML = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );

      if (updatedHTML !== null) {
        target.innerHTML = updatedHTML;
      }
    });

    if (
      this.header &&
      typeof this.header.reveal === 'function'
    ) {
      this.header.reveal();
    }

    this.open();

    this.refreshCart();
  }

  /* SHOPIFY SECTION TARGETS */

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector:
          `[id="cart-notification-product-${this.cartItemKey}"]`
      },
      {
        id: 'cart-notification-button'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    if (typeof html !== 'string') {
      return null;
    }

    const parsedDocument = new DOMParser()
      .parseFromString(html, 'text/html');

    const element = parsedDocument.querySelector(
      selector
    );

    return element ? element.innerHTML : null;
  }

  /* FETCH COMPLETE CART */

  async fetchCart() {
    const response = await fetch(
      this.getCartUrl('get'),
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error(
        `Cart request failed: ${response.status}`
      );
    }

    const cart = await response.json();

    if (
      !cart ||
      !Array.isArray(cart.items)
    ) {
      throw new Error(
        'Invalid Shopify cart response.'
      );
    }

    return cart;
  }

  /* REFRESH COMPLETE CART */

  async refreshCart() {
    const currentRequest = ++this.requestSequence;

    this.setText(
      '#zeb-cart-count',
      'Updating your shopping bag...'
    );

    if (this.itemsContainer) {
      this.itemsContainer.setAttribute(
        'aria-busy',
        'true'
      );
    }

    try {
      const cart = await this.fetchCart();

      if (currentRequest !== this.requestSequence) {
        return;
      }

      if (cart.currency) {
        this.currency = cart.currency;
      }

      this.renderCart(cart);

    } catch (error) {
      if (currentRequest !== this.requestSequence) {
        return;
      }

      console.error(
        'Zebaesh Cart Fetch Error:',
        error
      );

      this.setText(
        '#zeb-cart-count',
        'Unable to refresh cart'
      );

      this.showError(
        'We could not load your cart. Please try again.'
      );

    } finally {
      if (
        currentRequest === this.requestSequence &&
        this.itemsContainer
      ) {
        this.itemsContainer.setAttribute(
          'aria-busy',
          'false'
        );
      }
    }
  }

  /* MASTER CART RENDER */

  renderCart(cart) {
    const items = Array.isArray(cart.items)
      ? cart.items
      : [];

    const totalQuantity = Number(
      cart.item_count || 0
    );

    const lineCount = items.length;

    this.setText(
      '#zeb-cart-count',
      `${totalQuantity} ${
        totalQuantity === 1 ? 'item' : 'items'
      } in your shopping bag`
    );

    this.setText(
      '#zeb-cart-items-count',
      `${lineCount} ${
        lineCount === 1 ? 'Product' : 'Products'
      }`
    );

    this.renderCartItems(items);

    // Preview is instant and independent of PayFee network availability.
    // Cart pricing comes from Shopify's applied sale/bundle discounts.
    this.latestOriginalCart = cart;
    const pricing = this.getCartPricing(cart);
    const eligible = (cart.currency || this.currency) === 'PKR' &&
      pricing && pricing.baseSubtotal >= this.fixedPrepaymentReward;
    const reward = eligible ? this.fixedPrepaymentReward : null;
    this.discountAmount = reward;
    this.walletExpectedDiscount = reward;
    this.renderTotals(cart);
    this.updateVisibleVoucher();

    this.renderRecentProduct(items);

    this.updateActionState(totalQuantity);
  }

  /* RENDER ALL CART ITEMS */

  renderCartItems(items) {
    if (!this.itemsContainer) {
      return;
    }

    this.itemsContainer.replaceChildren();

    if (!items.length) {
      this.itemsContainer.appendChild(
        this.createElement(
          'div',
          'zeb-cart__empty',
          'Your shopping bag is currently empty.'
        )
      );

      return;
    }

    const fragment =
      document.createDocumentFragment();

    items.forEach((item) => {
      fragment.appendChild(
        this.createCartItem(item)
      );
    });

    this.itemsContainer.appendChild(fragment);
  }

  /* CREATE CART ITEM */

  createCartItem(item) {
    const card = this.createElement(
      'article',
      'zeb-cart__item'
    );

    const imageBox = this.createElement(
      'div',
      'zeb-cart__item-image'
    );

    const imageURL = this.getItemImage(item);

    if (imageURL) {
      const image = document.createElement('img');

      image.src = imageURL;
      image.alt =
        item.product_title || item.title || '';

      image.loading = 'lazy';

      imageBox.appendChild(image);
    }

    const details = this.createElement(
      'div',
      'zeb-cart__item-details'
    );

    const productName =
      item.product_title ||
      item.title ||
      'Product';

    const title = this.createElement(
      'h3',
      'zeb-cart__item-title',
      productName
    );

    details.appendChild(title);

    if (
      item.variant_title &&
      item.variant_title !== 'Default Title'
    ) {
      details.appendChild(
        this.createElement(
          'span',
          'zeb-cart__item-variant',
          item.variant_title
        )
      );
    }

    this.appendItemProperties(details, item);

    const sellingPlan =
      item.selling_plan_allocation
        ?.selling_plan
        ?.name;

    if (sellingPlan) {
      details.appendChild(
        this.createElement(
          'span',
          'zeb-cart__item-variant',
          sellingPlan
        )
      );
    }

    const quantity = Number(item.quantity) || 0;

    const quantityBox = this.createElement(
      'div',
      'zeb-cart__quantity'
    );

    const minus = this.createButton(
      'zeb-cart__quantity-btn',
      '−',
      'decrease',
      item.key,
      quantity
    );

    minus.setAttribute(
      'aria-label',
      `Decrease quantity of ${productName}`
    );

    const value = this.createElement(
      'span',
      'zeb-cart__quantity-value',
      quantity
    );

    const plus = this.createButton(
      'zeb-cart__quantity-btn',
      '+',
      'increase',
      item.key,
      quantity
    );

    plus.setAttribute(
      'aria-label',
      `Increase quantity of ${productName}`
    );

    quantityBox.append(
      minus,
      value,
      plus
    );

    details.appendChild(quantityBox);

    const actions = this.createElement(
      'div',
      'zeb-cart__item-actions'
    );

    const remove = this.createButton(
      'zeb-cart__remove',
      '',
      'remove',
      item.key
    );

    remove.setAttribute(
      'aria-label',
      `Remove ${productName} from cart`
    );

    remove.title = 'Remove item';

    remove.innerHTML = `
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M3 6h18" />
        <path d="M8 6V4.5C8 3.7 8.7 3 9.5 3h5c.8 0 1.5.7 1.5 1.5V6" />
        <path d="M5.5 6l1 14c.1.6.5 1 1.1 1h8.8c.6 0 1-.4 1.1-1l1-14" />
        <path d="M10 10v7" />
        <path d="M14 10v7" />
      </svg>
    `;

    // Show before-discount line price, matching the subtotal basis.
    // Shopify's final_line_price may already include the PayFee coupon.
    const originalUnit = Number(item.original_price);
    const quantityForPrice = Number(item.quantity);
    const originalLine = Number(item.original_line_price);
    const linePrice = Math.max(
      Number.isSafeInteger(originalLine) && originalLine >= 0 ? originalLine : 0,
      Number.isSafeInteger(originalUnit) && originalUnit >= 0 &&
        Number.isSafeInteger(quantityForPrice) && quantityForPrice >= 0
        ? originalUnit * quantityForPrice : 0
    );

    const price = this.createElement(
      'span',
      'zeb-cart__item-price',
      this.formatMoney(linePrice)
    );

    actions.append(
      remove,
      price
    );

    card.append(
      imageBox,
      details,
      actions
    );

    return card;
  }

  /* ITEM PROPERTIES */

  appendItemProperties(container, item) {
    const properties = item.properties;

    if (
      !properties ||
      typeof properties !== 'object'
    ) {
      return;
    }

    Object.entries(properties).forEach(
      ([name, value]) => {
        if (
          name.startsWith('_') ||
          value === null ||
          value === undefined ||
          value === ''
        ) {
          return;
        }

        const propertyValue = Array.isArray(value)
          ? value.join(', ')
          : String(value);

        if (
          propertyValue.startsWith('/') ||
          propertyValue.startsWith('https://') ||
          propertyValue.startsWith('http://')
        ) {
          return;
        }

        container.appendChild(
          this.createElement(
            'span',
            'zeb-cart__item-properties',
            `${name}: ${propertyValue}`
          )
        );
      }
    );
  }

  /* PRODUCT IMAGE HELPER */

  getItemImage(item) {
    return (
      item.featured_image?.url ||
      item.image ||
      item.featured_image?.src ||
      ''
    );
  }

  /* RECENTLY ADDED PRODUCT */

  renderRecentProduct(items) {
    const imageElement = this.find(
      '#zeb-cart-recent-image'
    );

    if (!imageElement) {
      return;
    }

    let recentItem = null;

    if (this.cartItemKey) {
      recentItem = items.find(
        (item) =>
          item.key === this.cartItemKey
      );
    }

    if (
      !recentItem &&
      this.recentVariantId
    ) {
      recentItem = items.find(
        (item) =>
          String(item.variant_id) ===
          String(this.recentVariantId)
      );
    }

    if (!recentItem) {
      recentItem = items[0] || null;
    }

    if (!recentItem) {
      this.heroImageRequestId++;
      imageElement.removeAttribute('src');

      imageElement.alt = '';

      this.setText(
        '#zeb-cart-recent-title',
        ''
      );

      this.setText(
        '#zeb-cart-recent-variant',
        ''
      );

      return;
    }

    // Show the existing product image immediately, then load the optional
    // public Shopify product metafield for the large hero image only.
    const imageURL = this.getItemImage(recentItem);

    if (imageURL) {
      imageElement.src = imageURL;
    } else {
      imageElement.removeAttribute('src');
    }

    this.updateRecentHeroImage(recentItem, imageElement);

    imageElement.alt =
      recentItem.product_title ||
      recentItem.title ||
      'Zebaesh Product';

    this.setText(
      '#zeb-cart-recent-title',
      recentItem.product_title ||
      recentItem.title ||
      ''
    );

    const variantTitle =
      recentItem.variant_title &&
      recentItem.variant_title !== 'Default Title'
        ? recentItem.variant_title
        : '';

    this.setText(
      '#zeb-cart-recent-variant',
      variantTitle
    );
  }

  /* CART HERO IMAGE — PRODUCT METAFIELD (PUBLIC STOREFRONT ONLY) */

  async updateRecentHeroImage(item, imageElement) {
    // A new request invalidates responses for older cart items.
    const requestId = ++this.heroImageRequestId;
    const handle = item?.handle;

    if (typeof handle !== 'string' ||
        !/^[a-z0-9][a-z0-9-]*$/i.test(handle)) {
      return; // Existing cart item image stays as fallback.
    }

    try {
      if (!this.heroImageCache.has(handle)) {
        const root = this.cartRoot.endsWith('/')
          ? this.cartRoot
          : `${this.cartRoot}/`;

        const request = fetch(
          `${root}products/${encodeURIComponent(handle)}?section_id=zeb-cart-hero-image`,
          {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'default',
            headers: { Accept: 'text/html' }
          }
        ).then(async (response) => {
          if (!response.ok) return null;

          const markup = await response.text();
          const doc = new DOMParser().parseFromString(markup, 'text/html');
          const node = doc.querySelector('[data-zeb-cart-hero-url]');
          const rawURL = node?.getAttribute('data-zeb-cart-hero-url');
          if (!rawURL) return null;

          // Reject script/data URLs and off-site image destinations.
          const url = new URL(rawURL, window.location.origin);
          const isShopifyCDN = url.hostname === 'cdn.shopify.com';
          const isStorefront = url.origin === window.location.origin;
          return url.protocol === 'https:' && (isShopifyCDN || isStorefront)
            ? url.href
            : null;
        }).catch(() => null);

        this.heroImageCache.set(handle, request);
      }

      const metafieldURL = await this.heroImageCache.get(handle);

      // Never let a slower previous request replace a newer product image.
      if (requestId !== this.heroImageRequestId ||
          !imageElement.isConnected || !metafieldURL) return;

      imageElement.src = metafieldURL;
    } catch (error) {
      // Metafield is optional: leave the original image in place.
      console.warn('Zebaesh cart hero image fallback used.', error);
    }
  }

  /* PRICING LEDGER — KEEP SALE, BUNDLE AND PAYFEE SEPARATE */

  isBundleDiscount(application) {
    const label = String(application?.title || application?.code || application?.name || '');
    return /pumper|bundle|volume\s*discount|quantity\s*(?:break|discount)|tier(?:ed)?\s*(?:price|discount)/i.test(label);
  }

  getPayfeeCartIdentity(cart) {
    return JSON.stringify((cart?.items || []).map(item => [
      String(item.variant_id || ''), Number(item.quantity),
      Number(item.original_line_price), item.properties || {}
    ]).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));
  }

  rememberPayfeeCheckout(cart, code, amount) {
    try {
      sessionStorage.setItem('zeb-active-payfee-receipt-v4', JSON.stringify({
        identity: this.getPayfeeCartIdentity(cart), code, amount,
        timestamp: Date.now()
      }));
    } catch (_) {}
  }

  getRememberedPayfeeAmount(cart) {
    try {
      const receipt = JSON.parse(sessionStorage.getItem('zeb-active-payfee-receipt-v4') || 'null');
      if (!receipt || receipt.identity !== this.getPayfeeCartIdentity(cart) ||
          Date.now() - Number(receipt.timestamp) > 30 * 60 * 1000 ||
          !this.isPayfeeCode(receipt.code) ||
          !Number.isSafeInteger(receipt.amount) || receipt.amount <= 0) return null;
      return receipt.amount;
    } catch (_) { return null; }
  }

  getCartPricing(cart) {
    // All values use Shopify's minor currency units (paisa for PKR).
    // original_line_price reflects the sale price, NOT compare_at_price.
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const saleTotal = items.reduce((sum, item) => {
      const value = Number(item.original_line_price);
      return sum + (Number.isSafeInteger(value) && value >= 0 ? value : NaN);
    }, 0);
    const finalTotal = Number(cart?.total_price);
    if (!Number.isSafeInteger(saleTotal) || !Number.isSafeInteger(finalTotal) || finalTotal < 0) return null;

    // items_subtotal_price already includes line-level discounts such as
    // Pumper product discounts, but excludes cart-level discounts.
    const reportedItemsSubtotal = Number(cart?.items_subtotal_price);
    const lineSubtotal = Number.isSafeInteger(reportedItemsSubtotal) && reportedItemsSubtotal >= 0
      ? reportedItemsSubtotal
      : items.reduce((sum, item) => {
          const value = Number(item.final_line_price);
          return sum + (Number.isSafeInteger(value) && value >= 0 ? value : NaN);
        }, 0);
    if (!Number.isSafeInteger(lineSubtotal) || lineSubtotal < 0) return null;

    // Shopify exposes cart-level applications separately. Do not subtract
    // a cart-level allocation again when it's already in finalTotal.
    const applications = Array.isArray(cart?.cart_level_discount_applications)
      ? cart.cart_level_discount_applications : [];
    let otherCartDiscounts = 0;
    let payfeeCartDiscounts = 0;
    let bundleApplied = false;

    const isPaymentApplication = (app) => {
      const label = String(app?.code || app?.title || app?.name || '');
      const activeCodes = this.getAppliedDiscountCodes(cart).filter(code => this.isPayfeeCode(code));
      return this.isPayfeeCode(label) || /payfee|payment[ -]?method|prepaid[ -]?(?:reward|discount)|online[ -]?payment[ -]?discount/i.test(label) ||
        activeCodes.some(code => label.toLowerCase() === code.toLowerCase());
    };

    for (const application of applications) {
      const amount = Number(application?.total_allocated_amount);
      if (!Number.isSafeInteger(amount) || amount <= 0) continue;
      if (isPaymentApplication(application)) payfeeCartDiscounts += amount;
      else {
        otherCartDiscounts += amount;
        if (this.isBundleDiscount(application)) bundleApplied = true;
      }
    }

    let payfeeLineDiscounts = 0;
    for (const item of items) {
      const allocations = item.line_level_discount_allocations || item.discount_allocations || [];
      for (const allocation of allocations) {
        const application = allocation.discount_application ||
          cart?.discount_applications?.[allocation.discount_application_index] || {};
        const amount = Number(allocation.amount);
        if (!Number.isSafeInteger(amount) || amount <= 0) continue;
        if (isPaymentApplication(application)) payfeeLineDiscounts += amount;
        else if (this.isBundleDiscount(application) ||
          (Number(item.quantity) > 1 && /^(?:10|15)%\s*off$/i.test(String(application?.title || '').trim()))) {
          bundleApplied = true;
        }
      }
      // An actual bundle label/properties are needed; quantity alone is NOT
      // proof of Pumper eligibility.
      if (Number(item.quantity) > 1 &&
          Object.entries(item.properties || {}).some(([name, value]) =>
            /pumper|bundle/i.test(String(name)) && value != null && value !== '')) {
        const original = Number(item.original_line_price);
        const final = Number(item.final_line_price);
        if (Number.isSafeInteger(original) && Number.isSafeInteger(final) && final < original) {
          bundleApplied = true;
        }
      }
    }

    // Bundle-base subtotal: line discounts + non-PayFee cart discounts.
    // PayFee may already be applied after returning from checkout; undo ONLY
    // its identified line-level allocation, never Pumper savings.
    let baseSubtotal = lineSubtotal + payfeeLineDiscounts - otherCartDiscounts;
    if (!Number.isSafeInteger(baseSubtotal) || baseSubtotal < 0) return null;

    // Cart's final total can include an already-applied PayFee discount
    // after returning from checkout. It is NOT the before-PayFee subtotal.
    // The item subtotal plus recognized non-payment cart discounts is the
    // authoritative base; never subtract the unexplained final-total gap.
    // In particular, a cached/hidden payment code must not lower this base.
    const identifiedPayfee = payfeeLineDiscounts + payfeeCartDiscounts;
    const extraFinalReduction = baseSubtotal - identifiedPayfee - finalTotal;
    if (extraFinalReduction > 0) {
      // Diagnostic only: keep original non-payment subtotal unchanged.
      // Unknown discount identity cannot be safely called 'Pumper'.
      console.info('[Zebaesh Cart] Final cart total has additional discount; retaining pre-payment subtotal.', {
        additionalMinorUnits: extraFinalReduction
      });
    }

    return {
      saleTotal,
      baseSubtotal,
      bundleApplied,
      payfeeAlreadyApplied: Math.max(0, baseSubtotal - finalTotal),
      finalTotal
    };
  }

  getCartBaseSubtotal(cart) {
    return this.getCartPricing(cart)?.baseSubtotal ?? null;
  }

  /* SUBTOTAL & PREPAID REWARD — NEVER SUBTRACT IT TWICE */

  renderTotals(cart) {
    const pricing = this.getCartPricing(cart);
    const badge = this.find('#zeb-cart-bundle-note');
    if (badge) badge.hidden = !pricing?.bundleApplied;

    if (!pricing) {
      for (const id of [
        '#zeb-cart-subtotal', '#zeb-cart-summary-discount',
        '#zeb-cart-summary-online', '#zeb-cart-cod-total',
        '#zeb-cart-online-total', '#zeb-cart-savings'
      ]) this.setText(id, '—');
      return;
    }
    const subtotal = pricing.baseSubtotal;
    // A fixed Rs. 100 prepaid preview, applied ONCE after the bundle subtotal.
    // COD displays the baseline without deducting this preview.
    const savings = (cart.currency || this.currency) === 'PKR' &&
      subtotal >= this.fixedPrepaymentReward
      ? this.fixedPrepaymentReward : null;
    const estimated = savings === null ? null : subtotal - savings;

    this.setText('#zeb-cart-subtotal', this.formatMoney(subtotal));
    this.setText('#zeb-cart-summary-discount', savings === null
      ? 'Not eligible'
      : `− ${this.formatMoney(savings)}`);
    this.setText('#zeb-cart-summary-online', estimated === null ? '—' : this.formatMoney(estimated));
    this.setText('#zeb-cart-cod-total', this.formatMoney(subtotal));
    this.setText('#zeb-cart-online-total', estimated === null ? '—' : this.formatMoney(estimated));
    this.setText('#zeb-cart-savings', savings === null ? '—' : this.formatMoney(savings));
  }

  /* CHECKOUT BUTTON STATE */

  updateActionState(itemCount) {
    const checkoutButton = this.find(
      '#zeb-cart-online-checkout'
    );

    if (checkoutButton) {
      checkoutButton.disabled = itemCount <= 0 || this.isUpdating || !!this.payfeeSubmitting;
    }
  }

  /* CHANGE CART QUANTITY */

  async changeQuantity(key, quantity) {
    if (
      this.isUpdating ||
      !key ||
      !Number.isInteger(quantity) ||
      quantity < 0
    ) {
      return;
    }

    this.isUpdating = true;

    this.setCartBusy(true);

    try {
      const response = await fetch(
        this.getCartUrl('change'),
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: key,
            quantity: quantity,
            sections: ['cart-icon-bubble', 'main-cart-footer'],
            sections_url: `${this.cartRoot}cart`
          })
        }
      );

      const updatedCart = await response.json();

      if (
        !response.ok ||
        updatedCart.status
      ) {
        throw new Error(
          updatedCart.description ||
          updatedCart.message ||
          'Unable to update cart quantity.'
        );
      }

      this.requestSequence++;

      if (updatedCart.currency) {
        this.currency = updatedCart.currency;
      }

      this.renderCart(updatedCart);
      this.latestCartItemCount = updatedCart.item_count;

      // Sync all visible storefront cart displays after Shopify confirms success.
      const bubbleHTML = updatedCart.sections?.['cart-icon-bubble'];
      if (!bubbleHTML || !this.updateCartBubble(bubbleHTML)) {
        await this.refreshCartBubble();
      }

      // Dawn's cart-items listens to this event and refreshes its own markup.
      this.publishCartUpdate(updatedCart);

      // Dawn's cart-items update event does not refresh the cart footer total.
      // Handle the footer separately only when an actual cart page is mounted.
      await this.syncCartPageFooter(updatedCart.sections?.['main-cart-footer']);

    } catch (error) {
      console.error(
        'Zebaesh Cart Update Error:',
        error
      );

      await this.refreshCart();

      this.showError(
        error.message ||
        'Unable to update your cart. Please try again.'
      );

    } finally {
      this.isUpdating = false;
      this.setCartBusy(false);
      // renderCart() ran while isUpdating=true and disabled checkout.
      // Recompute after the flag is cleared, including after a failure.
      this.updateActionState(Number(this.latestOriginalCart?.item_count) || 0);
    }
  }

  /* UPDATE HEADER CART ICON */

  updateCartBubble(html) {
    if (typeof html !== 'string' || !html.trim()) return false;

    const currentBubble = document.getElementById('cart-icon-bubble');
    if (!currentBubble) return false;

    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');

    // Shopify's cart-icon-bubble section usually contains icon markup only.
    // It does NOT necessarily include another #cart-icon-bubble element.
    const source = parsedDocument.querySelector('#cart-icon-bubble') ||
      parsedDocument.querySelector('#shopify-section-cart-icon-bubble') ||
      parsedDocument.querySelector('.shopify-section') ||
      parsedDocument.body;

    if (!source || !source.innerHTML.trim()) return false;

    // Same-origin Shopify-rendered section HTML; never interpolate cart text.
    currentBubble.innerHTML = source.innerHTML;
    currentBubble.classList.remove('hidden');
    return true;
  }

  /* LIVE CART PAGE FOOTER SYNC */

  async syncCartPageFooter(sectionHTML) {
    const footer = document.getElementById('main-cart-footer');
    if (!footer) return;

    let html = sectionHTML;
    if (typeof html !== 'string' || !html.trim()) {
      const response = await fetch(
        `${this.cartRoot}cart?section_id=main-cart-footer`,
        { credentials: 'same-origin', cache: 'no-store' }
      );
      if (!response.ok) return;
      html = await response.text();
    }

    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const updatedContents = parsedDocument.querySelector('#main-cart-footer .js-contents') ||
      parsedDocument.querySelector('.js-contents');
    const existingContents = footer.querySelector('.js-contents');

    if (updatedContents && existingContents) {
      existingContents.innerHTML = updatedContents.innerHTML;
    }

    // Use the authoritative cart item count rather than DOM timing when
    // Dawn is still independently refreshing its cart-items section.
    if (this.latestCartItemCount !== undefined) {
      footer.classList.toggle('is-empty', this.latestCartItemCount === 0);
    }
  }

  /* CART BUBBLE FALLBACK */

  async refreshCartBubble() {
    try {
      const root = this.cartRoot.endsWith('/')
        ? this.cartRoot
        : `${this.cartRoot}/`;

      const response = await fetch(
        `${root}cart?sections=cart-icon-bubble`,
        {
          credentials: 'same-origin',
          cache: 'no-store'
        }
      );

      if (!response.ok) {
        return;
      }

      const sections = await response.json();

      if (
        sections?.['cart-icon-bubble']
      ) {
        this.updateCartBubble(
          sections['cart-icon-bubble']
        );
      }

    } catch (error) {
      console.warn(
        'Zebaesh cart bubble refresh skipped:',
        error
      );
    }
  }

  /* DAWN CART UPDATE EVENT */

  publishCartUpdate(cart) {
    if (
      typeof publish === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'zeb-cart',
        cartData: cart,
        variantId: this.recentVariantId
      });
    }

  }

  /* CART BUSY STATE */

  setCartBusy(isBusy) {
    if (this.itemsContainer) {
      this.itemsContainer.setAttribute(
        'aria-busy',
        String(isBusy)
      );
    }

    this.querySelectorAll(
      '[data-action]'
    ).forEach((button) => {
      button.disabled = isBusy;
    });
  }

  /* ERROR HANDLING */

  showError(message) {
    if (!this.itemsContainer) {
      return;
    }

    const previousError = this.find(
      '.zeb-cart__error'
    );

    if (previousError) {
      previousError.remove();
    }

    const errorBox = this.createElement(
      'div',
      'zeb-cart__error',
      message
    );

    errorBox.setAttribute(
      'role',
      'alert'
    );

    errorBox.style.cssText = `
      padding: 12px 14px;
      margin-bottom: 10px;
      border: 1px solid rgba(180, 50, 50, 0.25);
      border-radius: 9px;
      background: #FFF1F1;
      color: #923C3C;
      font-size: 12px;
      line-height: 1.5;
    `;

    this.itemsContainer.prepend(errorBox);
  }

  /* DAWN COMPATIBILITY */

  setActiveElement(element) {
    this.activeElement = element;
  }
}

/* CUSTOM ELEMENT REGISTRATION */

if (!customElements.get('cart-notification')) {
  customElements.define(
    'cart-notification',
    CartNotification
  );
}
